/**
 * Partial-word and item-number matching, done in the browser.
 *
 * Shopify's search matches whole words and Danish stems. "kass" finds Eurokasse because the
 * stemmer reduces "kasse" to "kass"; "plas" finds nothing, because the indexed word is "plast"
 * and a fragment is not a stem of anything. Item numbers behave the same way: "10301" matches,
 * "10" does not. Both search endpoints were measured on this catalogue, with and without the
 * trailing-wildcard syntax and the prefix option - the wildcard makes it worse, because it turns
 * the stemmer off and then "kass" no longer reaches "Eurokasse" either.
 *
 * So fragments are matched here, against sections/search-index.liquid: id, title, item numbers
 * and the words worth searching, one line per product. The list is fetched once, on the first
 * search that needs it, and kept for the session. Matching is plain substring work - the point
 * is to find what the engine missed, not to replace it, so this only runs when the engine came
 * back with nothing.
 *
 * It also carries the shop's synonyms. Shopify has paused the creation of synonym groups in
 * Search & Discovery - the button is disabled, and the docs have not caught up - so the words a
 * customer uses for a product have nowhere else to be written down. They are applied only when
 * the literal term found nothing, so a synonym can add results but never displace better ones.
 *
 * Exposed as window.ZimpleSearchIndex:
 *   match(term, limit) -> Promise<Array<{i, t}>>   ids in the order they should be shown
 */
(function () {
  'use strict';

  /* Loaded by the header and by the search page, so on /search it runs twice. */
  if (window.ZimpleSearchIndex) return;

  const CACHE_KEY = 'zimple:search-index';
  /* Long enough that a customer's whole visit costs one request, short enough that a price or
     title changed in admin is not argued with for the rest of the day. */
  const CACHE_TTL = 30 * 60 * 1000;
  /* 250 products per request. A catalogue past this is better served by Search & Discovery than
     by a list in the browser, and the cap keeps a runaway fetch from being possible at all. */
  const MAX_PAGES = 20;
  const MIN_TERM = 2;

  let loading = null;
  let entries = null;

  /**
   * Lowercases and strips the accents that stop a customer typing "lag" from finding "låg".
   * Danish o-slash and ae do not decompose, so they are spelled out; everything else falls out
   * of the NFD decomposition. Anything left that is not a letter or digit becomes a separator,
   * which is what makes "400x300" and "400 × 300" the same string.
   * @param {string} value
   * @returns {string}
   */
  function fold(value) {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/ø/g, 'o')
      .replace(/æ/g, 'ae')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /**
   * Reads the synonym setting into two shapes: groups where every term finds every other, and
   * rules that only work in one direction.
   *
   *   bakke, kasse, beholder   -> all three find each other
   *   kunststof > plast        -> kunststof finds plast, plast does not find kunststof
   *   # a note                 -> ignored
   *
   * @param {string} raw
   * @returns {{groups: string[][], rules: Array<{from: string[], to: string[]}>}}
   */
  function parseSynonyms(raw) {
    const groups = [];
    const rules = [];

    (raw || '').split('\n').forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;

      const terms = (part) => part.split(',').map(fold).filter(Boolean);

      if (trimmed.includes('>')) {
        const [left, right] = trimmed.split('>');
        const from = terms(left);
        const to = terms(right);
        if (from.length && to.length) rules.push({ from, to });
        return;
      }

      const group = terms(trimmed);
      // A line with one term states no relationship, so there is nothing to record.
      if (group.length > 1) groups.push(group);
    });

    return { groups, rules };
  }

  /* A compound's head noun has to be at least this long before a synonym may match it from the
     front. Below it the folded alphabet starts colliding: "laag" folds to "lag", which sits
     inside "pluklager". Tails are safe at any length, because Danish compounds put the noun
     being named last - "kasse" in "eurokasse" really is a kasse. */
  const COMPOUND_HEAD_MIN = 5;

  /**
   * Whether a synonym stands in a piece of folded text.
   *
   * Not the plain substring test the typed term gets. A fragment someone is still typing is
   * meant to be partial - "plas" must reach "plast". A synonym is a whole word the shop wrote
   * down, so it has to land on a word, the tail of a compound, or the front of a long enough
   * one; anything looser turns a folded three-letter word into a wildcard.
   *
   * @param {string} text Folded.
   * @param {string} term Folded.
   * @returns {boolean}
   */
  function containsTerm(text, term) {
    const padded = ` ${text} `;
    /* Whole word, or the tail of a compound. */
    if (padded.includes(`${term} `)) return true;

    return term.length >= COMPOUND_HEAD_MIN && padded.includes(` ${term}`);
  }

  let synonyms = null;

  /**
   * Every term a word may stand in for, itself included.
   * @param {string} word Folded.
   * @returns {string[]}
   */
  function expand(word) {
    if (!synonyms) {
      const raw = window.theme && theme.settings ? theme.settings.searchSynonyms : '';
      synonyms = parseSynonyms(raw);
    }

    const out = new Set([word]);
    synonyms.groups.forEach((group) => {
      if (group.includes(word)) group.forEach((term) => out.add(term));
    });
    synonyms.rules.forEach((rule) => {
      if (rule.from.includes(word)) rule.to.forEach((term) => out.add(term));
    });

    return Array.from(out);
  }

  /**
   * @param {Object} product As rendered by sections/search-index.liquid.
   * @returns {Object} The same product with its folded forms alongside.
   */
  function prepare(product) {
    return {
      i: product.i,
      t: product.t,
      title: fold(product.t),
      skus: fold(product.s).split(' ').filter(Boolean),
      all: `${fold(product.t)} ${fold(product.s)} ${fold(product.k)}`
    };
  }

  function readCache(url) {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;

      const cached = JSON.parse(raw);
      /* The url carries the locale and market, so a cache from another one is not reused. */
      if (cached.url !== url || Date.now() - cached.at > CACHE_TTL) return null;

      return cached.products;
    } catch (error) {
      /* Private mode, or storage the browser has cleared. Fetching again is always correct. */
      return null;
    }
  }

  function writeCache(url, products) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ url, at: Date.now(), products }));
    } catch (error) {
      /* Over quota on a large catalogue. The index still works for this page load. */
    }
  }

  /**
   * Pulls one page of the index section and parses the JSON out of its wrapper.
   * @param {string} url
   * @param {number} page
   * @returns {Promise<Object>}
   */
  async function fetchPage(url, page) {
    const response = await fetch(`${url}&page=${page}`);
    if (!response.ok) throw new Error(response.status);

    /* Section Rendering API wraps the output in an element. Reading textContent rather than
       slicing the string keeps this working whatever wrapper tag the section is given. */
    const tmpl = document.createElement('template');
    tmpl.innerHTML = await response.text();

    return JSON.parse(tmpl.content.textContent.trim());
  }

  /**
   * @returns {Promise<Array>} The prepared index, loading it if this is the first call.
   */
  function load() {
    if (entries) return Promise.resolve(entries);
    if (loading) return loading;

    const url = window.theme && theme.routes ? theme.routes.searchIndex : null;
    if (!url) return Promise.resolve([]);

    loading = (async () => {
      const cached = readCache(url);
      if (cached) return cached;

      const first = await fetchPage(url, 1);
      let products = first.products;

      for (let page = 2; page <= Math.min(first.pages, MAX_PAGES); page += 1) {
        /* Sequential on purpose: this runs while someone is typing, and a burst of parallel
           requests would compete with the search request that actually fills the dropdown. */
        // eslint-disable-next-line no-await-in-loop
        const next = await fetchPage(url, page);
        products = products.concat(next.products);
      }

      writeCache(url, products);
      return products;
    })()
      .then((products) => {
        entries = products.map(prepare);
        return entries;
      })
      .catch(() => {
        /* Leave `entries` unset so a later search can try again, but do not retry within this
           one - a broken index must never stop the dropdown from showing what the engine found. */
        loading = null;
        return [];
      });

    return loading;
  }

  /**
   * Ranks a product against the query. Lower is better; null means no match.
   * @param {Object} entry
   * @param {string} query Folded, whole.
   * @param {string[]} words Folded, split.
   * @returns {?number}
   */
  function rank(entry, query, words) {
    if (!words.every((word) => entry.all.includes(word))) {
      /* Nothing matched as typed. A synonym may still stand in for one of the words - but this
         is always the weakest kind of match, so it is ranked below every literal one. A synonym
         found in the title still beats one found only in the description. */
      const inTitle = words.every((word) => expand(word).some((alt) => containsTerm(entry.title, alt)));
      if (inTitle) return 6;

      const anywhere = words.every((word) => expand(word).some((alt) => containsTerm(entry.all, alt)));
      return anywhere ? 7 : null;
    }

    /* An item number typed in full or in part is the least ambiguous thing a customer can do,
       so it outranks a title that merely happens to contain the same digits. */
    if (entry.skus.some((sku) => sku.startsWith(query))) return 0;
    if (entry.title.startsWith(query)) return 1;
    if (entry.title.split(' ').some((word) => word.startsWith(query))) return 2;
    if (entry.title.includes(query)) return 3;
    if (words.every((word) => entry.title.split(' ').some((titleWord) => titleWord.startsWith(word)))) return 4;

    /* Matched only through tags, collections or the description. */
    return 5;
  }

  /**
   * Finds products whose text contains the term, in the order they should be offered.
   * @param {string} term What the customer typed.
   * @param {number} limit Most results to return.
   * @returns {Promise<Array<{i: number, t: string}>>}
   */
  async function match(term, limit) {
    const query = fold(term);
    if (query.length < MIN_TERM) return [];

    const words = query.split(' ').filter(Boolean);
    const index = await load();

    const hits = index
      .map((entry) => ({ entry, score: rank(entry, query, words) }))
      .filter((hit) => hit.score !== null);

    /* A synonym is a loose enough match on its own; a synonym found only in a description is
       looser still. Folding makes that worse - "laag" and "lager" share three letters - so once
       a synonym has matched a title, the description-only ones are dropped rather than used to
       pad the list out to the limit. */
    const inTitle = hits.some((hit) => hit.score === 6);

    return hits
      .filter((hit) => !(inTitle && hit.score === 7))
      .sort((a, b) => a.score - b.score
        || a.entry.t.length - b.entry.t.length
        || a.entry.t.localeCompare(b.entry.t, 'da'))
      .slice(0, limit || 10)
      .map((hit) => ({ i: hit.entry.i, t: hit.entry.t }));
  }

  window.ZimpleSearchIndex = { match, fold, load, parseSynonyms };
})();
